import clsx from "clsx";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { RootState, useDispatch, useSelector } from "src/store";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { makeStyles } from "tss-react/mui";

import { selectGroupById } from "@dashboard/groups/store/selectors";
import { getDeviceBatteryIcon, getDeviceBatteryLevelText, HardwareIconColor } from "@dashboard/shared/battery";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { getMemoryAlert, getMemoryUsageIcon, getMemoryUsageText } from "@dashboard/shared/memory";
import { UpdateDevicePayload } from "@devices/api/devices";
import { setPaneTab, updateDevice } from "@devices/store";
import { License, PolicyStatus } from "@devices/types";
import {
    getActiveConnectionType,
    getDeviceConnectionIcon,
    getDeviceConnectionLabel,
    getDeviceOnlineSince,
    getDeviceUptime,
} from "@devices/utils/device";
import { getDevicePolicyStatus, getPolicyIconColor } from "@devices/utils/license";
import { Box, Theme, useTheme } from "@mui/material";
import { getSettings } from "@services/configService";
import TimeService from "@services/timeService/timeService";
import { TextLink } from "@shared/TextLink";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

import { GroupPropertyRow } from "./GroupPropertyRow";

function useConnectionIconColor(isOnline?: boolean) {
    const theme = useTheme();

    if (isOnline) {
        return theme.palette.emteria.success;
    }
    return theme.palette.emteria.error;
}

const useStyles = makeStyles<{ connectivityColor: string; policyIconColor: string }>()(
    (theme: Theme, { connectivityColor, policyIconColor }) => ({
        connectionIcon: { color: connectivityColor },
        policyIcon: { color: policyIconColor },
        iconPositive: {
            color: theme.palette.emteria.success,
        },
        iconWarning: {
            color: theme.palette.emteria.warning,
        },
        iconNegative: {
            color: theme.palette.emteria.error,
        },
        iconNeutral: {
            color: theme.palette.emteria.text.secondary,
        },
        propertyRow: {
            alignItems: "center",
            marginBottom: theme.spacing(25),
        },
        iconWithText: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(2),
        },
    }),
);

function DeviceOverviewTab(props: License): React.JSX.Element {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { lastState: deviceState } = props.device ?? { lastState: null };
    const { t } = useTranslation(["common", "sidepanels"]);

    const group = useSelector((state: RootState) => selectGroupById(state, props.groupId));
    const uptimeHumanReadable = getDeviceUptime(props.device);
    const onlineSinceHumanReadable = getDeviceOnlineSince(props.device);

    const memoryUsageIcon = getMemoryUsageIcon(deviceState?.memory);
    const memoryUsageText = getMemoryUsageText(deviceState?.memory);
    const memoryAlert = getMemoryAlert(deviceState?.memory);

    const policyStatusRaw = getDevicePolicyStatus(props.device, group);
    const isUniversal = Boolean(props.universalGroupName);

    const getPolicyNonComplianceReason = (): string | null => {
        if (policyStatusRaw !== PolicyStatus.NON_COMPLIANT) {
            return null;
        }
        if (!props.device?.lastState) {
            return t("sidepanels:devicePanel.policyReason.noDeviceData");
        }
        const policy = props.device.lastState.policy;
        if (!policy) {
            return t("sidepanels:devicePanel.policyReason.noPolicyData");
        }
        const isHashMismatch = Boolean(policy.hash && group?.policyHash && policy.hash !== group.policyHash);
        if (isHashMismatch) {
            return t("sidepanels:devicePanel.policyReason.updatePending");
        }
        const hasErrors = policy.errors ? Object.keys(policy.errors).length > 0 : false;
        if (hasErrors || policy.successful === false) {
            return t("sidepanels:devicePanel.policyReason.runtimeProblem");
        }
        return null;
    };

    const formatPolicyStatus = (): string => {
        if (policyStatusRaw === PolicyStatus.NO_GROUP_POLICY) {
            return (
                i18next.t("pages:devicePage.options.compliant") + " (" + i18next.t("pages:devicePage.options.noGroupPolicy") + ")"
            );
        }
        if (policyStatusRaw === PolicyStatus.COMPLIANT) {
            return i18next.t("pages:devicePage.options.compliant");
        }
        if (policyStatusRaw === PolicyStatus.NON_COMPLIANT) {
            const reason = getPolicyNonComplianceReason();
            return reason
                ? i18next.t("pages:devicePage.options.nonCompliant") + " (" + reason + ")"
                : i18next.t("pages:devicePage.options.nonCompliant");
        }
        return "-";
    };

    const policyStatus = formatPolicyStatus();

    const policyIconColor = getPolicyIconColor(policyStatusRaw, theme);
    const connectivityColor = useConnectionIconColor(props.device?.isOnline);
    const { classes } = useStyles({ connectivityColor, policyIconColor });

    const systemTabAvailable = getSettings().features.devicePanel.osTabEnabled && Boolean(deviceState);

    const iconColorClassMap = {
        [HardwareIconColor.SECONDARY]: classes.iconNeutral,
        [HardwareIconColor.SUCCESS]: classes.iconPositive,
        [HardwareIconColor.WARNING]: classes.iconWarning,
        [HardwareIconColor.ERROR]: classes.iconNegative,
    };

    const battery = deviceState?.battery;
    const batteryIcon = getDeviceBatteryIcon(battery);
    const batteryLevelText = getDeviceBatteryLevelText(battery);

    const isOnline = Boolean(props.device?.isOnline);
    const connectionType = getActiveConnectionType(deviceState?.networks, isOnline);
    const connectionIcon = getDeviceConnectionIcon(connectionType, isOnline);

    let connectionStatusText: string;
    if (isOnline) {
        const connectionLabel = getDeviceConnectionLabel(connectionType);
        connectionStatusText = connectionLabel
            ? t("sidepanels:devicePanel.status.onlineWithConnection", { connection: connectionLabel })
            : t("common:status.online");
    } else {
        connectionStatusText = t("sidepanels:devicePanel.status.offline");
    }

    const onUpdate = async (payload: Omit<UpdateDevicePayload, "id">) => {
        if (!props.device?.id) {
            return;
        }
        try {
            await dispatch(updateDevice({ id: props.device.id, ...payload })).unwrap();
            showSuccessToast(t("sidepanels:devicePanel.toast.updateSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.general")}</PaneElement.Header>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.activationCode")}</PropertyKey>
                    <PropertyValue>{props.activationCode || "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.universalStatus")}</PropertyKey>
                    <PropertyValue>
                        {isUniversal
                            ? t("sidepanels:devicePanel.overviewTab.universal")
                            : t("sidepanels:devicePanel.overviewTab.notUniversal")}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow style={{ alignItems: "center", marginBottom: 1 }}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.deviceSlotStatus")}</PropertyKey>
                    <PropertyValue>{t("common:status.used")}</PropertyValue>
                </PropertyRow>
                <GroupPropertyRow id={props.id} groupId={props.groupId} groupName={props.groupName} />
            </PaneElement.Section>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.device")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("common:label.id")}</PropertyKey>
                    <PropertyValue>{props.device?.id ?? "-"}</PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.name")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableInput
                            initialValue={props.device?.name ?? ""}
                            onSave={async (val) => {
                                await onUpdate({
                                    name: val,
                                    description: props.device?.description ?? null,
                                    comment: props.comment ?? null,
                                });
                            }}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.comment")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableInput
                            initialValue={props.comment ?? ""}
                            onSave={async (val) => {
                                await onUpdate({
                                    comment: val,
                                    name: props.device?.name ?? null,
                                    description: props.device?.description ?? null,
                                });
                            }}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.description")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.EditableInput
                            initialValue={props.device?.description ?? ""}
                            onSave={async (val) => {
                                await onUpdate({
                                    description: val,
                                    name: props.device?.name ?? null,
                                    comment: props.comment ?? null,
                                });
                            }}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.serialNumber")}</PropertyKey>
                    <PropertyValue>
                        <PaneElement.CopyableValue value={props.device?.serial} />
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.imei")}</PropertyKey>
                    <PropertyValue>{deviceState?.telephony?.imei ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.product")}</PaneElement.Header>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("common:label.codename")}</PropertyKey>
                    <PropertyValue>{props.device?.product ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.model")}</PropertyKey>
                    <PropertyValue>{deviceState?.build?.model ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section withoutBorder>
                <PaneElement.Header>{t("sidepanels:devicePanel.overviewTab.health")}</PaneElement.Header>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.connectionStatus")}</PropertyKey>
                    <PropertyValue>
                        <Box className={classes.iconWithText}>
                            <i className={clsx("fa-solid", connectionIcon, classes.connectionIcon)} />
                            <span>{connectionStatusText}</span>
                        </Box>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.groupPolicy")}</PropertyKey>
                    <PropertyValue>
                        {systemTabAvailable ? (
                            <TextLink
                                onClick={() => dispatch(setPaneTab("OS"))}
                                tooltip={t("sidepanels:devicePanel.tooltip.showPolicyDetails")}
                                sx={{ display: "flex", alignItems: "center", gap: 2 }}
                            >
                                <i className={clsx("fa-solid fa-gears", classes.policyIcon)} />
                                <span>{policyStatus ?? "-"}</span>
                            </TextLink>
                        ) : (
                            <Box component="span" className={classes.iconWithText}>
                                <i className={clsx("fa-solid fa-gears", classes.policyIcon)} />
                                <span>{policyStatus ?? "-"}</span>
                            </Box>
                        )}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.usedMemory")}</PropertyKey>
                    <PropertyValue>
                        <Box className={classes.iconWithText}>
                            {memoryAlert.isLow ? (
                                <>
                                    <i className={clsx("fa-solid", memoryAlert.icon, iconColorClassMap[memoryAlert.color])} />
                                    <span>{t("sidepanels:devicePanel.overviewTab.memoryLowAlert")}</span>
                                </>
                            ) : (
                                <>
                                    <i
                                        className={clsx(
                                            "fa-solid",
                                            memoryUsageIcon.icon,
                                            iconColorClassMap[memoryUsageIcon.color],
                                        )}
                                    />
                                    <span>{memoryUsageText}</span>
                                </>
                            )}
                        </Box>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.batteryLevel")}</PropertyKey>
                    <PropertyValue>
                        <Box className={classes.iconWithText}>
                            <i className={clsx("fa-solid", batteryIcon.icon, iconColorClassMap[batteryIcon.color])} />
                            <span>{batteryLevelText}</span>
                        </Box>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.deviceUptime")}</PropertyKey>
                    <PropertyValue>{uptimeHumanReadable ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.onlineSince")}</PropertyKey>
                    <PropertyValue>{onlineSinceHumanReadable ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.propertyRow}>
                    <PropertyKey>{t("sidepanels:devicePanel.overviewTab.lastActivity")}</PropertyKey>
                    <PropertyValue>
                        {props.device?.lastActivityDate
                            ? TimeService.getHumanReadableTimeDifference(props.device?.lastActivityDate, {
                                  zone: "UTC",
                                  withAffix: true,
                              })
                            : "-"}
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>
        </PaneElement.Container>
    );
}

export default DeviceOverviewTab;
